<template>
  <div class="line-chart-box" >
    <!-- 图例 -->
    <fund-chart-legend
      :is-touched="isTouched"
      :period="period"
      :basic-info="basicInfo"
      :detail-info="detailInfo"
      :line-chart-type="lineChartType"
      :legend-value-list="legendValueList"
      :index-name="indexName"
      @clickIndexPoint="() => { this.showIndexPopup = true }"
      @clickTipPoint="clickTipPoint"
      @clickIcon2="clickIcon2"/>
    <!-- 图表 -->
    <div class="chart-padding">
      <div class="chart-box" id="chart-box">
          <line-chart
            :width="chartWidth"
            :height="chartHeight"
            :request="getData"
            :initial="true"
            :config="lineChartConfig"
            @tooltipChange="lineChange"
            @tooltipHide="touchEnd"
            ref="chart"
            />
      </div>
    </div>
    <!-- 指数弹窗 -->
    <div v-if="showIndexPopup" class="index-pop-box">
      <div class="index-pop-container">
        <div
          v-for="(item, index) in indexList"
          :key="index"
          :style="{ color: item.color || '#000000'}"
          @click="clickIndexItem(item)"
          class="index-pop-item"
          >{{item.indexName}}</div>
        <div @click="() => { this.showIndexPopup = false }" class="index-pop-item index-pop-cancel"
          >取消</div>
      </div>
    </div>
    <!-- 提示弹窗 -->
    <legend-tip-modal
      :modal-type="tipModalType"
      :detail-info="detailInfo"
      @clickCancel="() => { this.tipModalType = '' }"
      @clickConfirm="()=> {
        this.tipModalType = ''
        $goFundApp()
      }" />
    <!-- 旗子点击弹窗 -->
    <!-- 注：净值数据无expansion所以不会有分红点，累计收益只有31 32 200 => 所以不会有特殊旗子 -->
    <flag-alert-modal v-if="showFlagModal"
      :content="modalContent"
      :btn-config="btnConfig"
      @clickLeft="() => { this.showFlagModal = false; this.$goFundApp(); }"
      @clickRight="() => { this.showFlagModal = false; }" />
  </div>
</template>

<script>
import tool from '@/utils/tool';
import { throttle } from '@fund/basic-lib/dist/lib/function';
import isEmpty from '@fund/basic-lib/dist/lib/lang/isEmpty';
import { LineChart } from '@fund/f2-chart-h5';
import dayjs from 'dayjs';
import CHART_CONFIG from './chart-config';
import FlagAlertModal from './FlagAlertModal.vue';
import FundChartLegend from './FundChartLegend.vue';
import LegendTipModal from './LegendTipModal.vue';

export default {

  name: 'fund-line-chart',

  components: {
    LineChart,
    FundChartLegend,
    LegendTipModal,
    FlagAlertModal,
  },

  props: {
    lineChartType: {
      type: String,
      default: '',
    },
    fundCode: {
      type: String,
      default: '',
    },
    basicInfo: {
      type: Object,
      default: () => ({}),
    },
    detailInfo: {
      type: Object,
      default: () => ({}),
    },
    period: {
      type: String,
      default: '',
    },
    indexInfo: {
      type: Object,
      default: () => ({}),
    },
  },

  data() {
    return {
      indexCode: '', // 当前选中指数
      indexName: '', // 当前选中指数
      chartWidth: 0,
      chartHeight: 0,
      requestRes: {}, // 接口数据
      isTouched: false, // 图表touch状态
      originLegendValueList: [], // 未按住时图例数据
      legendValueList: [], // 图例数据列表
      showIndexPopup: false, // 展示指数弹窗
      tipModalType: '', // 提示弹窗类型
      showFlagModal: false, // 展示旗子弹窗
      modalContent: '', // 旗子弹窗内容
      btnConfig: { // 旗子弹窗按钮配置
        leftText: '详情',
        leftTextColor: '#ff4400',
        rightText: '知道了',
        rightTextColor: '#000000',
      },
      mainPointCount: 0, // 折线图主线点数
      lineLabels: [], // 图表最大值最小值
      specialPoints: [], // 特殊点
      flagList: [], // 旗子
    };
  },

  computed: {
    requestParams() { // 图表接口参数
      return {
        FCODE: this.fundCode,
        INDEXCODE: this.indexCode,
        RANGE: this.period,
        POINTCOUNT: ['3n', '5n', 'ln'].indexOf(this.period) >= 0 ? 500 : '',
      };
    },

    chartConfig() {
      return {
        // // 图表宽度
        chartWidth: this.chartWidth,
        // // 图表高度
        chartHeight: this.chartHeight,
        ...(CHART_CONFIG.chartConfig[this.lineChartType] || {}),
        tooltip: {
          main: ['type', 'fund-point'],
        },
      };
    },

    indexList() {
      return this.formatIndexList(this.indexCode, this.indexName);
    },

    lineChartConfig() { // lr版图标传参config
      return {
        ...CHART_CONFIG.lineChartConfig[this.lineChartType],
        labels: this.lineLabels,
        points: this.specialPoints,
        flags: this.flagList,
      };
    },
  },

  async mounted() {
    // 图表适配宽度
    const boxEle = document.getElementById('chart-box');
    this.chartWidth = boxEle.offsetWidth;
    this.chartHeight = boxEle.offsetHeight;
  },

  watch: {
    period() { // 选中时间范围变化
      this.refreshChart();
    },

    lineChartType() { // 选中图表类型发生变化
      this.refreshChart();
    },

    detailInfo: {
      handler() {
        this.refreshChart();
      },
      deep: true,
    },
  },

  methods: {
    // 刷新图标
    refreshChart: throttle(function refreshChart() {
      this.$refs.chart.start();
    }, 100, { leading: false, trailing: true }),

    clickIcon2() {
      this.$emit('changeAvg');
    },
    // 根据lineChartType获取图表数据
    async getData() {
      // await this.$refs.chart?.chart?.clear();
      // await this.$refs.chart?.reset();
      switch (this.lineChartType) {
        case 'accProfit': // 累计收益
          return this.getAccProfit();
        case 'accProfit2': // 累计收益
          return this.getAccProfit();
        case 'perNetValue': // 单位净值
          return this.getPerNetValue(['navPointList']);
        case 'netFixed': // 净值修复
          return this.getPerNetValue(['historyValueList']);
        case 'annualizationOf7D': // 七日年化
          return this.getPerNetValue(['historyValueList', 'valueRateList'], 'history-net-value');
        case 'profitOf10000': // 万元收益
          return this.getPerNetValue(['netValueList']);
        default: return [];
      }
    },

    // 获取缓存数据
    getCacheData(cacheKey) {
      const curTime = new Date().getTime();
      const resCache = JSON.parse(localStorage.getItem(cacheKey)) || {};
      const lastTime = resCache?.lastTime || 0;
      if (curTime - lastTime > 3 * 60 * 60 * 1000 // 缓存过期
        || this.$isEmpty(resCache)) return {};
      return resCache;
    },

    // accProfit-累计收益
    async getAccProfit() {
      console.log('===> getAccProfit', this.lineChartType);
      if (this.lineChartType === 'accProfit2') {
        const cacheKey = `ttjj-acc-${this.fundCode}-${this.period}`;
        const resCache = this.getCacheData(cacheKey);
        // const resCache = null;
        let res = resCache;
        if (this.$isEmpty(resCache)) { // 缓存为空
          res = await this.$http.useRequests('FundComApi.getHighEndAccEnjoy', this.requestParams)
            .catch((err) => { console.log('getAccProfit ERR', err); }) || {};
          localStorage.setItem(cacheKey, JSON.stringify({
            ...res,
            lastTime: new Date().getTime(),
          }));
        }
        this.requestRes = res;
        const isHighEndEnjoy = this.detailInfo?.isHighEndEnjoy;
        const {
          sylPointList = [],
          sePointList = [],
          sameTypeAvgCanBuyPointList = [],
        } = res;
        this.mainPointCount = sylPointList.length; // 主线条点数
        this.lineLabels = this.getMinMaxLabels(sylPointList); // 设置极值标签
        const mergeList = [
          ...sePointList,
          ...(isHighEndEnjoy ? sameTypeAvgCanBuyPointList : []),
          ...sylPointList,
        ].filter((item) => !this.$isEmpty(item.value));
        this.afterGetData();
        this.specialPoints = this.getSpecialPoints(sylPointList);
        this.flagList = this.getSpecialFlags(sylPointList);
        return mergeList;
      }
      const cacheKey = `ttjj-acc-${this.fundCode}-${this.period}`;
      const resCache = this.getCacheData(cacheKey);
      // const resCache = null;
      let res = resCache;
      if (this.$isEmpty(resCache)) { // 缓存为空
        res = await this.$http.useRequests('FundComApi.getHighEndAccEnjoy', this.requestParams)
          .catch((err) => { console.log('getAccProfit ERR', err); }) || {};
        localStorage.setItem(cacheKey, JSON.stringify({
          ...res,
          lastTime: new Date().getTime(),
        }));
      }
      this.requestRes = res;
      console.log('origin data', this.requestRes);
      const {
        sylPointList = [],
        sePointList = [],
        sameTypeAvgPointList = [],
      } = res;
      this.mainPointCount = sylPointList.length; // 主线条点数
      this.lineLabels = this.getMinMaxLabels(sylPointList); // 设置极值标签
      const isHighEndEnjoy = this.detailInfo?.isHighEndEnjoy;
      console.log('isHighEndEnjoy', isHighEndEnjoy);
      const mergeList = [
        ...sePointList,
        ...(isHighEndEnjoy ? sameTypeAvgPointList : []),
        ...sylPointList,
      ].filter((item) => !this.$isEmpty(item.value));
      this.afterGetData();
      this.specialPoints = this.getSpecialPoints(sylPointList);
      this.flagList = this.getSpecialFlags(sylPointList);
      console.log(' ==> mergeList', mergeList);
      return mergeList;
    },

    // perNetValue-单位净值
    async getPerNetValue(valueTypeList = [], mainKey = '') {
      const cacheKey = `ttjj-pernet-${this.fundCode}-${this.period}`;
      const resCache = this.getCacheData(cacheKey);
      // const resCache = null;
      let res = resCache;
      if (this.$isEmpty(resCache)) { // 缓存为空
        res = await this.$http.useRequests('FundComApi.getHighEndDiagram', this.requestParams)
          .catch((err) => { console.log('getPerNetValue ERR', err); });
        localStorage.setItem(cacheKey, JSON.stringify({
          ...res,
          lastTime: new Date().getTime(),
        }));
      }
      this.requestRes = res;
      console.log('origin data', this.requestRes);
      const mergeList = [];
      valueTypeList.forEach((key) => {
        mergeList.push(...(res[key] || []));
      });
      console.log('===> mergeList', mergeList, valueTypeList);
      if (valueTypeList.length > 1) { // 有2条线及以上
        const mainPointList = mergeList.filter((point) => point.type === mainKey);
        this.lineLabels = this.getMinMaxLabels(mainPointList); // 设置极值标签
        this.mainPointCount = mainPointList.length;
        this.specialPoints = this.getSpecialPoints(mainPointList);
        this.flagList = this.getSpecialFlags(mainPointList);
      } else {
        this.lineLabels = this.getMinMaxLabels(mergeList); // 设置极值标签
        this.mainPointCount = mergeList.length;
        this.specialPoints = this.getSpecialPoints(mergeList);
        this.flagList = this.getSpecialFlags(mergeList);
      }
      this.afterGetData();
      console.log('getPerNetValue', mergeList);
      return mergeList;
    },

    // 图表获取数据后处理：1、图例 2、特殊点和旗子
    afterGetData() {
      // 图例数据列表
      const valueList = [];
      const valueKeys = CHART_CONFIG.pointKeyList[this.lineChartType] || [];
      console.log('====> valueKeys', valueKeys, this.requestRes);
      valueKeys.forEach((key) => {
        const keyList = (this.requestRes[key] || [])
          .sort((a, b) => dayjs(a.date).valueOf() - dayjs(b.date).valueOf());
        console.log('====> keyList', keyList);
        if (!this.$isEmpty(keyList)) {
          valueList.push({ ...keyList[keyList.length - 1] });
        }
      });

      console.log('====> legend value point', valueList);
      this.originLegendValueList = valueList;
      this.legendValueList = valueList;
      // 选中指数
      const { indexInfo } = this.requestRes?.extraInfo || {};
      console.log('===> indexInfo', this.requestRes?.extraInfo);
      if (!this.$isEmpty(indexInfo) && !this.$isEmpty(indexInfo.code)) {
        this.indexCode = indexInfo.code;
        this.indexName = indexInfo.name;
      }
      // 处理特殊点和旗子
      // this.handleSpecialFlags(dataList);
      // this.handleSpecialPoints(dataList);
    },

    // 获取最大值最小值标签配置
    getMinMaxLabels(pointList) {
      if (this.$isEmpty(pointList)) return [];
      const sortedList = pointList.filter((item) => !this.$isEmpty(item.value))?.sort( // 升序排序
        (point1, point2) => (parseFloat(point1.value) - parseFloat(point2.value)),
      );
      const minPoint = sortedList[0];
      const maxPoint = sortedList[sortedList.length - 1];
      const formatter = CHART_CONFIG.lineChartConfig[this.lineChartType]?.yScale.formatter
        || ((value) => value);
      return [{
        position: [minPoint.date, minPoint.value],
        content: formatter(minPoint.value),
      }, {
        position: [maxPoint.date, maxPoint.value],
        content: formatter(maxPoint.value),
      }];
    },

    // 处理特殊点-h5场景仅处理非交易场景
    getSpecialPoints(dataList) {
      // if (this.$isEmpty(this.requestRes?.extraInfo?.changePoints)) return dataList;
      const { // 分红、基金经理变更、巨额赎回
        // dividend = [],
        fundManagerChange = [],
        largeRedemption = [],
      } = this.requestRes?.extraInfo?.changePoints || {};
      const dividend = this.lineChartType === 'perNetValue' // 仅单位净值展示分红点位
        ? this.requestRes?.extraInfo?.changePoints?.dividend || [] : [];
      // 基金经理
      const pointList = [
        ...dividend?.map((point) => ({ type: 'dividend', point })),
        ...fundManagerChange?.map((point) => ({ type: 'managerChange', point })),
        ...largeRedemption?.map((point) => ({ type: 'largeRedemption', point })),
      ];
      const colorMap = CHART_CONFIG.chartPointMap;
      const tempPointList = pointList.map((item) => {
        let target = dataList.find((i) => i.date === item.point);
        if (this.$isEmpty(target) || this.$isEmpty(target.value)) {
          const nearIndex = this.findNearPointIndex(dataList, item.point);
          target = dataList[nearIndex] || {};
        }
        return !this.$isEmpty(target)
          ? ({ position: [target.date, target.value], style: { fill: colorMap[item.type] } }) : {};
      }).filter((item) => !this.$isEmpty(item));
      console.log('===> tempPointList', pointList, tempPointList);
      return tempPointList || [];
      // this.$refs.chart.drawSpecialPoint(tempPointList);
    },

    // 处理旗子
    getSpecialFlags(dataList) {
      const { // 分红、基金经理变更、巨额赎回
        // dividend = [],
        fundManagerChange = [],
        largeRedemption = [],
      } = this.requestRes?.extraInfo?.changePoints || {};
      const dividend = this.lineChartType === 'perNetValue' // 仅单位净值展示分红旗子
        ? this.requestRes?.extraInfo?.changePoints?.dividend : [];
      const lastManagerPoint = fundManagerChange?.[0] || '';
      const lastDividendPoint = dividend?.[0] || '';
      const lastRedemption = largeRedemption?.[0] || '';
      const lastPoints = [{
        type: 'largeRedemption',
        point: lastRedemption,
      }, {
        type: 'managerChange',
        point: lastManagerPoint,
      }, {
        type: 'dividend',
        point: lastDividendPoint,
      }].filter((item) => !this.$isEmpty(item.point));
      const flagList = [];
      const lineList = [];
      const pointList = [];
      const flagMap = CHART_CONFIG.chartFlagMap;
      console.log('lastPoints', lastPoints);
      lastPoints.forEach((item, index) => {
        if (flagMap.has(item.type)) {
          const { flagColor, text, lineColor } = flagMap.get(item.type);
          let targetIndex = dataList.findIndex((i) => i.date === item.point);
          let nearItem = {};
          if (targetIndex < 0 || this.$isEmpty(dataList[targetIndex].value)) { // 旗子位置没有点，取临近前一个点
            const nearIndex = this.findNearPointIndex(dataList, item.point);
            targetIndex = nearIndex;
            nearItem = dataList[nearIndex]; // 最近的点
          }
          const flagDirection = targetIndex < (this.mainPointCount?.length / 2); // 控制旗子左右方向 true-右
          const radius = flagDirection
            ? `${index === 2 ? 0 : '2px'} 2px 2px ${index === 2 ? '2px' : 0}`
            : `2px ${index === 2 ? 0 : '2px'} ${index === 2 ? '2px' : 0} 2px`;
          const htmlStyle = `width: max-content;font-size: 12px;color: #FFFFFF;opacity:0.85;padding:1px 2px; background-color:${flagColor}; border-radius:${radius}`;
          pointList.push({
            position: [!nearItem.date ? item.point : nearItem.date,
              !nearItem.date ? dataList[targetIndex].value : nearItem.value],
            content: `${text}>`,
            background: { fill: flagColor },
            line: { stroke: flagColor },
            onClick: () => {
              // this.clickFlag(item);
              setTimeout(() => {
                this.clickFlag(item);
              }, 300);
            },
          });
          flagList.push({
          // 控制旗子下降位置
            position(start, end) {
              const standard = end?.[0].ticks.slice().sort((a, b) => b - a);
              return [
                !nearItem.date ? item.point : nearItem.date,
                // eslint-disable-next-line no-nested-ternary
                !index ? 'max' : index === 1 ? standard[1] : 'min',
              ];
            },
            html: `<div style="${htmlStyle};"><span style="">${text}</span></div> `,
            alignX: flagDirection ? 'left' : 'right',
            // eslint-disable-next-line no-nested-ternary
            offsetX: index === 2 ? (flagDirection ? 0.5 : -3) : flagDirection ? 0 : -3,
            offsetY: index === 2 ? -6 : 6,
            // onClick: () => {
            //   this.clickFlag(item);
            // },
          });
          lineList.push({
            start: [!nearItem.date ? item.point : nearItem.date, 'min'],
            end(start, end) {
              const standard = end?.[0]?.ticks?.slice()?.sort((a, b) => b - a);
              return [
                !nearItem.date ? item.point : nearItem.date,
                index === 1 ? standard[1] : end?.[0].max,
              ];
            },
            style: {
            // 控制旗杆渐变方向
              stroke: `l(${index === 2 ? '270' : '90'}) 0:${lineColor} 1:#ffffff`,
              lineWidth: 1,
              lineCap: 'round',
            },
          });
        }
      });
      // this.$refs.chart.drawSpecialFlags(flagList, lineList);
      console.log('flagList', pointList);
      return pointList;
    },

    lineChange(e) {
      const specialLegendList = ['accProfit', 'accProfit2', 'perNetValue', 'trendOfGold', 'profitOf10000'];
      if (specialLegendList.includes(this.lineChartType)) { // 图例数据无画线类型特殊处理
        const date = e?.[0]?.date || '--';
        const valueKeys = CHART_CONFIG.pointKeyList[this.lineChartType] || [];
        const legendList = [];
        valueKeys.forEach((key) => {
          const valueList = this.requestRes[key];
          const legendPoint = valueList.find((item) => item.date === date) || {};
          legendList.push(legendPoint);
        });
        this.legendValueList = legendList;
        this.isTouched = true;
        return;
      }
      this.legendValueList = e;
      this.isTouched = true;
    },

    touchEnd() {
      this.isTouched = false;
      this.legendValueList = this.originLegendValueList;
    },

    clickFlag(item) {
      if (item.type === 'largeRedemption') { // 巨额赎回
        this.modalContent = `${item.point} 巨额赎回`;
        this.showFlagModal = true;
      }
      if (item.type === 'managerChange') { // 基金经理变更
        this.modalContent = `${item.point} 基金经理发生变更`;
        this.showFlagModal = true;
      }
      if (item.type === 'dividend') { // 基金分红
        this.modalContent = `${item.point}基金分红`;
        this.showFlagModal = true;
      }
    },

    // 选择指数
    clickIndexItem(item) {
      const { indexCode, indexName } = item;
      this.indexCode = indexCode;
      this.indexName = indexName;
      this.refreshChart();
      this.showIndexPopup = false;
    },

    clickTipPoint(item) {
      console.log('clickTipPoint===');
      if (item.type === 'sameTypeAvgCanBuy-point'
      || item.type === 'sameTypeAvg-point') {
        console.log('clickTipPoint===', this.tipModalType);
        const { updateDateCanBuy, updateDate } = this.indexInfo?.stageRiseListIndexInfo?.[0] ?? {};
        const updateTime = this.isChecked ? updateDateCanBuy : updateDate;
        const content = `平台从产品策略维度划定同类范围，计算平均值、对比同类涨幅做参考。\n在售同类，指平台代销的、未售罄的同一策略的产品；同类，指平台代销的同一策略的产品。\n在售同类数值每个交易日定时更新，最新更新时间${isEmpty(updateTime) ? '--' : updateDate}。`;
        multi.$feedback.showModal({
          content,
          showCancel: false,
          confirmText: '知道了',
          confirmColor: '#e93801',
        });
      } else {
        this.tipModalType = item.type;
      }
    },

    // 处理指数弹窗列表
    formatIndexList(indexCode, indexName) {
      const defaultList = CHART_CONFIG.indexPopupList;
      if (this.$isEmpty(indexCode) || this.$isEmpty(indexName)) return defaultList;
      let insertList = [];
      if (!defaultList.some((item) => item.indexCode === indexCode)) { // 非默认列表中包含的指数
        insertList = indexCode === 'H11001' || indexCode === '000832' ? [
          {
            indexName,
            indexCode,
            color: '#000000',
          },
          {
            indexName: '中债新综合财富指数',
            indexCode: 'CBA00101',
            color: '#000000',
          },
        ] : [{
          indexName,
          indexCode,
          color: '#000000',
        }];
      }
      return [...insertList, ...defaultList].map((item) => ({
        ...item,
        color: item.indexCode === this.indexCode ? '#ff4400' : '#000000',
      }));
    },

    // 找到日期最近的前一个点
    findNearPointIndex(list, date) {
      const firstDay = list?.[0];
      const days = tool.getDaysBetween(firstDay.date, date);
      let index = -1;
      let today = date;
      for (let i = 0; i < days; i += 1) {
        const yesterday = tool.getYesterday(today);
        index = list.findIndex((item) => (item.date === yesterday));
        if (index >= 0) return index;
        today = yesterday;
      }
      return index;
    },

    // 寻找附近的值
    findNearestValue(list, index) {
      console.log('===> find near index', index);
      if (index === 0) { // 仅向后找
        let item = {};
        for (let i = index + 1; i < list.length; i += 1) {
          if (!this.$isEmpty(list[i]?.value)) {
            item = list[i];
            break;
          }
        }
        return item.value || '--';
      }
      // 向前找
      let item = {};
      for (let i = index - 1; i >= 0; i -= 1) {
        if (!this.$isEmpty(list[i]?.value)) {
          item = list[i];
          break;
        }
      }
      if (!this.$isEmpty(item.value)) return item.value || '--';
      // 向后找
      for (let i = index + 1; i < list.length; i += 1) {
        if (!this.$isEmpty(list[i]?.value)) {
          item = list[i];
          break;
        }
      }
      return item.value || '--';
    },

  },
};
</script>

<style lang="less" scoped>
.line-chart-box {
  box-sizing: border-box;
  padding: 0;
  width: 100%;
  margin-top: 12px;
  margin-bottom: 24px;
}
.chart-padding {
  padding: 0 24px;
}
.chart-box {
  width: 100%;
  height: 400px;
}
.index-pop-box {
  position: fixed;
  background-color: rgba(0, 0, 0, 0.5);
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 10000;
}
.index-pop-container {
  position: absolute;
  bottom: 0;
  width: 100%;
  background-color: #f5f5f5;
}
.index-pop-item {
  background-color: #ffffff;
  font-size: 32px;
  color: #000000;
  text-align: center;
  line-height: 104px;
  border-bottom: 2px solid #f5f5f5;
}
.index-pop-cancel {
  margin-top: 10px;
}
</style>
